// Themed time picker — replaces native <input type="time">, whose popup can't
// be styled. Trigger looks like an Input; the panel is a scrollable list of
// 15-minute steps rendered with the app's theme tokens.

import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import clsx from 'clsx';
import { to12h } from '@/services/appointments.service';
import { Popover } from './Popover';

const STEP_MIN = 15;
const OPTIONS: string[] = Array.from({ length: (24 * 60) / STEP_MIN }, (_, i) => {
  const m = i * STEP_MIN;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
});

interface Props {
  value: string;                 // "HH:mm"
  onChange: (v: string) => void;
  className?: string;
  /** options before this time render disabled (e.g. window end > start) */
  min?: string;
}

const TimePicker = ({ value, onChange, className, min }: Props) => {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // scroll the selected option into view when the panel opens
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      listRef.current?.querySelector<HTMLButtonElement>("[data-selected='true']")
        ?.scrollIntoView({ block: 'center' });
    }, 0);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      className={className}
      panelClassName="w-[148px] p-1.5"
      trigger={
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={clsx(
            'h-9 w-full rounded-lg border border-border bg-surface px-3 text-[13px] text-text-primary',
            'flex items-center justify-between gap-2 cursor-pointer transition-colors',
            'hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          )}
        >
          <span className={clsx(!value && 'text-text-muted')}>{value ? to12h(value) : 'Pick time'}</span>
          <Clock size={13} className="text-text-muted shrink-0" />
        </button>
      }
    >
      <div ref={listRef} className="max-h-[228px] overflow-y-auto flex flex-col gap-0.5 pr-0.5">
        {OPTIONS.map((t) => {
          const selected = t === value;
          const disabled = !!min && t <= min;
          return (
            <button
              key={t}
              type="button"
              data-selected={selected || undefined}
              disabled={disabled}
              onClick={() => { onChange(t); setOpen(false); }}
              className={clsx(
                'w-full text-left rounded-md px-2.5 py-1.5 text-[12.5px] border-none transition-colors',
                selected
                  ? 'bg-primary text-on-primary font-semibold cursor-default'
                  : disabled
                    ? 'text-text-muted/35 cursor-not-allowed bg-transparent'
                    : 'text-text-primary bg-transparent hover:bg-surface-alt cursor-pointer',
              )}
            >
              {to12h(t)}
            </button>
          );
        })}
      </div>
    </Popover>
  );
};

export default TimePicker;
